import type { CSSProperties } from "react";
import { useMemo, useState, useEffect } from "react";
import { PumpPortalWalletFundingBadge } from "@/components/PumpPortalWalletFundingBadge";
import { Tooltip } from "@/components/Tooltip";
import { fetchSolanaRpc } from "@/lib/solanaRpc";
import { Loader2, Copy, Check, Sun, Moon, ArrowLeftRight, Search, Bell, Zap, Target } from "lucide-react";
import { useApp } from "@/context/AppCtx";
import { useWallet } from "@/context/WalletContext";
import { usePumpPortalConfigRevision } from "@/hooks/usePumpPortalConfigRevision";
import { usePumpPortalLinkedWalletSol } from "@/hooks/usePumpPortalLinkedWalletSol";
import { useIsMobile } from "@/hooks/useIsMobile";
import { getPumpPortalTradingWalletPubkey } from "@/lib/pumpPortalConfig";
import { homePath } from "@/lib/siteUrls";
import { ProfileDropdown } from "@/components/ProfileDropdown";
import { fetchWalletStats, WalletStats } from "@/lib/walletIntelligence";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { LanguageSelector } from "@/components/LanguageSelector";
import { SpotlightSearch } from "@/components/SpotlightSearch";

const noDrag = { WebkitAppRegion: "no-drag" } as CSSProperties;

export function AppTopChrome() {
  const isMobile = useIsMobile();
  const [pumpPortalCopied, setPumpPortalCopied] = useState(false);
  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);

  // Listen for Cmd+K / Ctrl+K to open Spotlight search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === "k") {
        e.preventDefault();
        setIsSpotlightOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const {
    activitySection,
    setActivitySection,
    setSidebarMode,
    setMobileActiveTab,
    theme,
    toggleTheme,
    layoutMode,
    setLayoutMode,
  } = useApp() as any;

  const { t } = useTranslation();

  const { connected, publicKey } = useWallet();
  const { solPrice } = useApp() as any;

  const pumpPortalRev = usePumpPortalConfigRevision();

  const topBarPortalLinkedPk = useMemo(() => getPumpPortalTradingWalletPubkey(), [pumpPortalRev]);

  const {
    sol: topBarPortalSol,
    loading: topBarPortalSolLoading,
  } = usePumpPortalLinkedWalletSol(topBarPortalLinkedPk);

  const [walletStats, setWalletStats] = useState<WalletStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [tps, setTps] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchTps = async () => {
      try {
        const res = await fetchSolanaRpc({
          jsonrpc: "2.0",
          id: 1,
          method: "getRecentPerformanceSamples",
          params: [1],
        });
        if (!res.ok) return;
        const data = await res.json();
        const sample = data.result?.[0];
        if (sample && sample.samplePeriodSecs > 0) {
          if (isMounted) setTps(Math.round(sample.numTransactions / sample.samplePeriodSecs));
        }
      } catch (e) {
        // ignore
      }
    };
    fetchTps();
    const interval = setInterval(fetchTps, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (connected && publicKey) {
      setLoadingStats(true);
      fetchWalletStats(publicKey).then((stats) => {
        if (stats) setWalletStats(stats);
        setLoadingStats(false);
      });
    } else {
      setWalletStats(null);
    }
  }, [connected, publicKey]);

  const handleCopyPumpPortal = () => {
    if (topBarPortalLinkedPk) {
      navigator.clipboard.writeText(topBarPortalLinkedPk);
      setPumpPortalCopied(true);
      setTimeout(() => setPumpPortalCopied(false), 2000);
    }
  };

  return (
    <header className={`${isMobile ? "fixed top-0 left-0 right-0 z-[200] bg-black/40 backdrop-blur-xl border-b border-white/10" : "flex flex-col border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"}`}>
      {/* Mobile navbar */}
      {isMobile ? (
        <div id="mobile-top-chrome" className="flex h-14 items-center justify-between px-3" style={noDrag}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileActiveTab("nursery")}
              className="flex items-center gap-1.5 font-serif font-bold text-lg text-foreground hover:opacity-80 transition-opacity"
              title="Go to Nursery"
            >
              <Target className="size-5 text-emerald-400" />
              <span className="italic tracking-tight">theScope</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile: Phantom wallet balance */}
            {connected && publicKey ? (
              <span className="flex items-center gap-1 rounded-full border border-indigo-500/35 bg-indigo-500/10 px-2 py-0.5 text-[8px] font-semibold tabular-nums text-indigo-200">
                {loadingStats ? (
                  <Loader2 className="size-2.5 animate-spin" />
                ) : (
                  <>
                    <span>{(walletStats?.solBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })} SOL</span>
                  </>
                )}
              </span>
            ) : null}
            {/* Mobile: PumpPortal wallet balance */}
            {topBarPortalLinkedPk ? (
              <PumpPortalWalletFundingBadge
                sol={topBarPortalSol}
                loading={topBarPortalSolLoading}
                pubkey={topBarPortalLinkedPk}
                compact
              />
            ) : null}
            <button
              onClick={() => setIsSpotlightOpen(true)}
              className="flex items-center justify-center rounded-full p-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 transition-all text-emerald-400 hover:text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.1)]"
              title={t("search.placeholder")}
            >
              <Search className="size-4" />
            </button>
            {/* Mobile: Deploy button */}
            <button
              onClick={() => {
                window.history.pushState({}, "", "/deploy");
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-emerald-500/20 to-emerald-400/10 border border-emerald-500/30 hover:bg-emerald-500/30 transition-all"
            >
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Deploy</span>
            </button>
            <LanguageSelector />
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center rounded-full p-1.5 hover:bg-white/5 transition-colors text-foreground/70 hover:text-foreground"
              title={t("tooltip.theme")}
            >
              {theme === "light" ? <Moon className="size-4" /> : <Sun className="size-4" />}
            </button>
            <button
              onClick={() => setLayoutMode(layoutMode === "lily" ? "standard" : "lily")}
              className="flex items-center justify-center rounded-full p-1.5 hover:bg-white/5 transition-colors text-foreground/70 hover:text-foreground"
              title="Toggle Layout Direction"
            >
              <ArrowLeftRight className="size-4" />
            </button>
            <ProfileDropdown />
          </div>
        </div>
      ) : (
        /* Desktop navbar */
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-4 flex-1">
            {/* Brand name */}
            <a
              href={homePath()}
              className="flex items-center gap-2 font-serif font-bold text-xl text-foreground hover:opacity-80 transition-opacity"
              title="Back to home"
            >
              <Target className="size-6 text-emerald-400" />
              <span className="italic tracking-tight">theScope</span>
            </a>
            
            {/* Nav tabs */}
            <nav className="flex items-center gap-1 rounded-lg bg-muted/50 p-1">

              {/* 
              <Tooltip text={t("nav.proxy")} side="bottom" delay={500}>
                  <NavTab
                    label={t("nav.proxy")}
                    active={activitySection === "proxy"}
                    onClick={() => {
                      setActivitySection("proxy");
                      setSidebarMode("proxy");
                    }}
                  />
                </Tooltip>
              */}
              {/* 
              <Tooltip text="Squads" side="bottom" delay={500}>
                  <NavTab
                    label="Squads"
                    active={activitySection === "fomo"}
                    onClick={() => {
                      setActivitySection("fomo");
                      setSidebarMode("fomo");
                    }}
                  />
                </Tooltip>
              */}
              <Tooltip text="P2P Escrow" side="bottom" delay={500}>
                  <NavTab
                    label="P2P Escrow"
                    active={activitySection === "p2p"}
                    onClick={() => {
                      setActivitySection("p2p");
                      setSidebarMode("p2p");
                    }}
                  />
                </Tooltip>
            </nav>

             {/* Spotlight Search Trigger */}
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 hidden md:flex items-center z-40 w-full max-w-[240px] lg:max-w-[400px]">
              <button
                onClick={() => setIsSpotlightOpen(true)}
                className="group flex items-center gap-3 w-full h-10 bg-white/[0.03] border border-white/10 rounded-xl px-4 text-left text-sm font-sans text-white/50 hover:text-white/80 hover:border-emerald-500/50 hover:bg-emerald-500/5 hover:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all cursor-text select-none"
              >
                <Search className="size-4 text-emerald-500/70 group-hover:text-emerald-400 transition-colors" />
                <span className="flex-1 truncate font-medium tracking-wide">{t("search.placeholder")}</span>
                <div className="hidden lg:flex items-center gap-1">
                  <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-bold font-sans text-white/40 border border-white/10 group-hover:border-emerald-500/30 group-hover:text-emerald-400/80 transition-colors">⌘</span>
                  <span className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-bold font-sans text-white/40 border border-white/10 group-hover:border-emerald-500/30 group-hover:text-emerald-400/80 transition-colors">K</span>
                </div>
              </button>
            </div>

            {/* Deploy Toolbar Slot (Injected by AlgoDeployWorkspace) */}
            <div id="top-chrome-deploy-slot" className="flex items-center gap-2 empty:hidden ml-2" />
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-4" style={noDrag}>
            
            {/* Network Status & SOL Price */}
            <div className="hidden lg:flex items-center gap-3 px-3 py-1 rounded-full bg-white/[0.02] border border-white/5 mr-1">
              <Tooltip text={t("tooltip.tps")} side="bottom">
                <div className="flex items-center gap-1.5 cursor-help">
                  <div className="relative flex size-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                  </div>
                  <span className="text-[10px] font-mono font-bold text-white/60">{tps !== null ? tps.toLocaleString() : "..."} TPS</span>
                </div>
              </Tooltip>
              <div className="w-[1px] h-3 bg-white/10" />
              <Tooltip text={t("tooltip.solPrice")} side="bottom">
                <span className="text-[10px] font-mono font-bold text-emerald-400 cursor-help">
                  ${(solPrice || 150).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Tooltip>
            </div>

            <div className="flex items-center gap-2">
              {connected && publicKey ? (
                <Tooltip
                  text={`Phantom wallet balance. Address: ${publicKey.slice(0, 6)}…${publicKey.slice(-4)}`}
                  side="bottom"
                >
                  <span className="flex items-center gap-1.5 rounded-full border border-indigo-500/35 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-indigo-200">
                    {loadingStats ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <>
                        <span>{(walletStats?.solBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })} SOL</span>
                        <span className="opacity-40">/</span>
                        <span className="text-white/80">${((walletStats?.solBalance || 0) * (solPrice || 150)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </>
                    )}
                  </span>
                </Tooltip>
              ) : null}
              {topBarPortalLinkedPk ? (
                <div className="flex items-center gap-2">
                  <Tooltip
                    text={`PumpPortal trading wallet balance. Address: ${topBarPortalLinkedPk.slice(0, 6)}…${topBarPortalLinkedPk.slice(-4)}`}
                    side="bottom"
                  >
                    <PumpPortalWalletFundingBadge
                      sol={topBarPortalSol}
                      loading={topBarPortalSolLoading}
                      pubkey={topBarPortalLinkedPk}
                      compact
                    />
                  </Tooltip>
                  <Tooltip text={t("tooltip.copyAddress")} side="bottom">
                    <button
                      onClick={handleCopyPumpPortal}
                      className="flex items-center justify-center rounded-full p-1 hover:bg-white/5 transition-colors group"
                    >
                      {pumpPortalCopied ? (
                        <Check className="size-3 text-emerald-400" />
                      ) : (
                        <Copy className="size-3 text-white/40 group-hover:text-white/60 transition-colors" />
                      )}
                    </button>
                  </Tooltip>
                </div>
              ) : null}
            </div>
            
            <div className="pl-3 border-l border-white/10 flex items-center gap-3">
              {/* Notification Bell */}
              <button className="relative flex items-center justify-center rounded-full p-1.5 hover:bg-white/5 transition-colors text-white/40 hover:text-white" title={t("tooltip.notifications")}>
                <Bell className="size-4" />
                <span className="absolute top-1.5 right-1.5 size-1.5 bg-rose-500 rounded-full border border-[#0b0e11]" />
              </button>

              {/* Quick Action Button */}
              <button 
                onClick={() => {
                  window.history.pushState({}, "", "/deploy");
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 active:scale-95 transition-all group/deploy"
              >
                <Zap className="size-3.5 text-emerald-400 group-hover/deploy:fill-emerald-400" />
                <span className="text-[11px] font-black uppercase tracking-widest text-emerald-400 group-hover/deploy:text-emerald-300">{t("nav.deploy")}</span>
              </button>
              <LanguageSelector />
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center rounded-full p-1.5 hover:bg-foreground/5 transition-colors text-foreground/70 hover:text-foreground"
                title={t("tooltip.theme")}
              >
                {theme === "light" ? <Moon className="size-4" /> : <Sun className="size-4" />}
              </button>
              <button
                onClick={() => setLayoutMode(layoutMode === "lily" ? "standard" : "lily")}
                className="flex items-center justify-center rounded-full p-1.5 hover:bg-foreground/5 transition-colors text-foreground/70 hover:text-foreground"
                title={t("tooltip.layout")}
              >
                <ArrowLeftRight className="size-4" />
              </button>
              <ProfileDropdown />
            </div>
          </div>
        </div>
      )}
      <SpotlightSearch isOpen={isSpotlightOpen} onClose={() => setIsSpotlightOpen(false)} />
    </header>
  );
}

function NavTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
